import React, { useState, useEffect } from 'react';
import {
  Settings, Bell, Shield, Globe, Database, Key,
  Save, RefreshCw, Copy, AlertTriangle, ClipboardCheck,
  Microscope, Loader2, Mail, Smartphone
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { api } from '../../lib/api';
import { toast } from 'sonner';

const notificationTypes = [
  { key: 'safety_event', label: 'Safety Events', description: 'Incident reports from facilities', icon: AlertTriangle, color: 'bg-red-100 text-red-600' },
  { key: 'audit', label: 'Audits', description: 'Audit scheduling and results', icon: ClipboardCheck, color: 'bg-blue-100 text-blue-600' },
  { key: 'risk', label: 'Risk Register', description: 'New and escalated risks', icon: Shield, color: 'bg-orange-100 text-orange-600' },
  { key: 'infection', label: 'Infection Control', description: 'HAI reports and outbreaks', icon: Microscope, color: 'bg-purple-100 text-purple-600' },
  { key: 'system', label: 'System & Business', description: 'Signups, payments, platform alerts', icon: Settings, color: 'bg-gray-100 text-gray-600' },
];

const channelList = [
  { key: 'bell', label: 'In-App', icon: Bell },
  { key: 'email', label: 'Email', icon: Mail },
  { key: 'push', label: 'Push', icon: Smartphone },
];

const SettingsPage = () => {
  const [settings, setSettings] = useState({
    platformName: 'NikoSafe Connect',
    supportEmail: 'support@nikosafeconnect.com',
    enableMFA: true,
    enableAuditLogs: true,
    enableAutoBackup: true,
    sessionTimeout: 30,
    maxLoginAttempts: 5,
    dataRetentionDays: 365,
    aiModel: 'gpt-5.2',
    maxTokensPerQuery: 2000,
  });

  const [prefs, setPrefs] = useState(null);
  const [prefsLoading, setPrefsLoading] = useState(true);
  const [prefsDirty, setPrefsDirty] = useState(false);
  const [prefsSaving, setPrefsSaving] = useState(false);

  useEffect(() => {
    api.get('/api/users/me/notification-preferences')
      .then(setPrefs)
      .catch(() => {
        const defaults = {};
        notificationTypes.forEach(t => { defaults[t.key] = { bell: true, email: false, push: false }; });
        setPrefs(defaults);
      })
      .finally(() => setPrefsLoading(false));
  }, []);

  const handleTogglePref = (type, channel) => {
    setPrefs(prev => ({
      ...prev,
      [type]: { ...prev[type], [channel]: !prev[type][channel] }
    }));
    setPrefsDirty(true);
  };

  const handleSavePrefs = async () => {
    setPrefsSaving(true);
    try {
      const updated = await api.put('/api/users/me/notification-preferences', prefs);
      setPrefs(updated);
      setPrefsDirty(false);
      toast.success('Notification preferences saved');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setPrefsSaving(false);
    }
  };

  const enableAll = () => {
    const next = {};
    notificationTypes.forEach(t => { next[t.key] = { bell: true, email: true, push: true }; });
    setPrefs(next);
    setPrefsDirty(true);
  };

  const disableAll = () => {
    const next = {};
    notificationTypes.forEach(t => { next[t.key] = { bell: false, email: false, push: false }; });
    setPrefs(next);
    setPrefsDirty(true);
  };

  const countEnabled = (channel) => {
    if (!prefs) return 0;
    return notificationTypes.filter(t => prefs[t.key]?.[channel]).length;
  };

  const handleSaveSettings = () => {
    toast.success('Settings saved successfully!');
  };

  return (
    <div className="space-y-6" data-testid="owner-settings-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-gray-900">Platform Settings</h1>
          <p className="text-gray-500 mt-1">Configure platform settings, notification delivery, and preferences</p>
        </div>
        <Button onClick={handleSaveSettings} className="btn-primary gap-2">
          <Save className="w-4 h-4" />
          Save Changes
        </Button>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* General Settings */}
          <div className="bg-white rounded-2xl border border-gray-100 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-[var(--niko-teal)]/10 flex items-center justify-center">
                <Settings className="w-5 h-5 text-[var(--niko-teal)]" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">General Settings</h2>
                <p className="text-sm text-gray-500">Basic platform configuration</p>
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <Label>Platform Name</Label>
                <Input value={settings.platformName} onChange={(e) => setSettings({...settings, platformName: e.target.value})} className="mt-1" />
              </div>
              <div>
                <Label>Support Email</Label>
                <Input type="email" value={settings.supportEmail} onChange={(e) => setSettings({...settings, supportEmail: e.target.value})} className="mt-1" />
              </div>
            </div>
          </div>

          {/* Notification Preferences Matrix */}
          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-yellow-100 flex items-center justify-center">
                    <Bell className="w-5 h-5 text-yellow-600" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">Notification Preferences</h2>
                    <p className="text-sm text-gray-500">Choose notifications and delivery channels</p>
                  </div>
                </div>
                {prefsDirty && (
                  <Button onClick={handleSavePrefs} disabled={prefsSaving} size="sm" className="btn-primary gap-2" data-testid="save-notif-prefs-btn">
                    {prefsSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                    Save
                  </Button>
                )}
              </div>
            </div>

            {prefsLoading ? (
              <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-[var(--niko-teal)]" /></div>
            ) : (
              <>
                {/* Channel Headers */}
                <div className="grid grid-cols-[1fr_auto] items-center px-5 py-2.5 bg-gray-50 border-b border-gray-100">
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Type</span>
                  <div className="flex items-center gap-1.5">
                    {channelList.map(ch => (
                      <div key={ch.key} className="w-[64px] flex flex-col items-center gap-0.5">
                        <ch.icon className="w-3.5 h-3.5 text-gray-500" />
                        <span className="text-[9px] font-semibold text-gray-500 uppercase">{ch.label}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Rows */}
                <div className="divide-y divide-gray-100">
                  {notificationTypes.map((type) => {
                    const Icon = type.icon;
                    const typePrefs = prefs?.[type.key] || {};
                    return (
                      <div key={type.key} className="grid grid-cols-[1fr_auto] items-center px-5 py-3" data-testid={`notification-pref-${type.key}`}>
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${type.color}`}>
                            <Icon className="w-4 h-4" />
                          </div>
                          <div>
                            <p className="font-medium text-sm text-gray-900">{type.label}</p>
                            <p className="text-xs text-gray-500">{type.description}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1.5">
                          {channelList.map(ch => (
                            <div key={ch.key} className="w-[64px] flex justify-center">
                              <Switch
                                checked={typePrefs[ch.key] ?? false}
                                onCheckedChange={() => handleTogglePref(type.key, ch.key)}
                                data-testid={`toggle-${type.key}-${ch.key}`}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Quick Actions */}
                <div className="px-5 py-2.5 border-t border-gray-100 bg-gray-50/50 flex items-center gap-3">
                  <button onClick={enableAll} className="text-xs text-[var(--niko-teal)] font-medium hover:underline" data-testid="enable-all-btn">Enable All</button>
                  <span className="text-gray-300">|</span>
                  <button onClick={disableAll} className="text-xs text-gray-500 font-medium hover:underline" data-testid="disable-all-btn">Disable All</button>
                </div>
              </>
            )}
          </div>

          {/* Security Settings */}
          <div className="bg-white rounded-2xl border border-gray-100 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center">
                <Shield className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Security Settings</h2>
                <p className="text-sm text-gray-500">Authentication and access control</p>
              </div>
            </div>
            <div className="space-y-4">
              <div className="flex items-center justify-between py-3 border-b border-gray-100">
                <div>
                  <p className="font-medium text-gray-900">Multi-Factor Authentication</p>
                  <p className="text-sm text-gray-500">Require MFA for all admin users</p>
                </div>
                <Switch checked={settings.enableMFA} onCheckedChange={(checked) => setSettings({...settings, enableMFA: checked})} />
              </div>
              <div className="flex items-center justify-between py-3 border-b border-gray-100">
                <div>
                  <p className="font-medium text-gray-900">Audit Logging</p>
                  <p className="text-sm text-gray-500">Log all user actions for compliance</p>
                </div>
                <Switch checked={settings.enableAuditLogs} onCheckedChange={(checked) => setSettings({...settings, enableAuditLogs: checked})} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Session Timeout (min)</Label><Input type="number" value={settings.sessionTimeout} onChange={(e) => setSettings({...settings, sessionTimeout: parseInt(e.target.value)})} className="mt-1" /></div>
                <div><Label>Max Login Attempts</Label><Input type="number" value={settings.maxLoginAttempts} onChange={(e) => setSettings({...settings, maxLoginAttempts: parseInt(e.target.value)})} className="mt-1" /></div>
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* AI Settings */}
          <div className="bg-white rounded-2xl border border-gray-100 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
                <RefreshCw className="w-5 h-5 text-purple-600" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900">Ask Niko AI</h2>
            </div>
            <div className="space-y-4">
              <div>
                <Label>AI Model</Label>
                <select value={settings.aiModel} onChange={(e) => setSettings({...settings, aiModel: e.target.value})} className="w-full mt-1 h-10 px-3 rounded-md border border-input bg-background text-sm">
                  <option value="gpt-5.2">GPT-5.2 (Recommended)</option>
                  <option value="gpt-4.1">GPT-4.1</option>
                  <option value="claude-sonnet">Claude Sonnet 4.5</option>
                </select>
              </div>
              <div><Label>Max Tokens per Query</Label><Input type="number" value={settings.maxTokensPerQuery} onChange={(e) => setSettings({...settings, maxTokensPerQuery: parseInt(e.target.value)})} className="mt-1" /></div>
            </div>
          </div>

          {/* Data Management */}
          <div className="bg-white rounded-2xl border border-gray-100 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
                <Database className="w-5 h-5 text-green-600" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900">Data Management</h2>
            </div>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-700">Auto Backup</span>
                <Switch checked={settings.enableAutoBackup} onCheckedChange={(checked) => setSettings({...settings, enableAutoBackup: checked})} />
              </div>
              <div><Label>Data Retention (days)</Label><Input type="number" value={settings.dataRetentionDays} onChange={(e) => setSettings({...settings, dataRetentionDays: parseInt(e.target.value)})} className="mt-1" /></div>
            </div>
          </div>

          {/* API Keys */}
          <div className="bg-white rounded-2xl border border-gray-100 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                <Key className="w-5 h-5 text-blue-600" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900">API Keys</h2>
            </div>
            <div className="space-y-3">
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500 mb-1">Production API Key</p>
                <div className="flex items-center gap-2">
                  <code className="text-xs text-gray-700">niko_prod_***********</code>
                  <button className="p-1 text-gray-400 hover:text-gray-600"><Copy className="w-3 h-3" /></button>
                </div>
              </div>
              <Button variant="outline" className="w-full gap-2" size="sm">
                <RefreshCw className="w-3 h-3" />
                Regenerate Keys
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
